import { CSSProperties } from 'outpost/preact';
import { Button } from '../base-components/button.tsx';
import { Row } from '../base-components/container.tsx';
import { App } from '../components/app.tsx';
import { BoardLinkComponent } from './board-link-component.tsx';
import { BoardLocalData } from './board-local-data.ts';
import { Board } from './board.ts';

export type BoardHeaderProps = {
    localData: BoardLocalData;
    isGuest: boolean;
    style?: CSSProperties;
};

export function BoardHeader(props: BoardHeaderProps) {
    let { localData, isGuest } = props;
    let style: CSSProperties = {
        // backgroundColor: 'pink',
        // padding: '5px',
        maxHeight: '25px',
        ...props.style
    };

    let hasOngoingAction = localData.currentInteraction !== null;
    let buttonStyle: CSSProperties = {
        pointerEvents: hasOngoingAction ? 'none' : undefined
    };

    let onClickFlip = () => {
        localData.setInteraction(card => {
            localData.board.flipCard(card);
            localData.notifyBoardUpdated();
            setTimeout(() => localData.scheduleShowTooltip(card));
        });
    };

    let onClickRotate = () => {
        localData.setInteraction(card => {
            localData.board.rotateCard(card);
            localData.notifyBoardUpdated();
        });
    };

    let onClickReveal = () => {
        localData.setInteraction(card => {
            localData.board.revealCard(card);
            localData.notifyBoardUpdated();
            setTimeout(() => localData.scheduleShowTooltip(card));
        });
    };

    let onClickMark = () => {
        localData.setInteraction(card => {
            localData.board.markCard(card, localData.playerId);
            localData.notifyBoardUpdated();
        });
    };

    let onClickClear = () => {
        [...localData.board.cards()].forEach(card => card.marks = []);
        localData.notifyBoardUpdated();
    };

    // let onClickDraw = () => {
    //     localData.setInteraction(card => {
    //         localData.board.moveCardToZone(card, localData.board.players[card.ownerId].zones.hand);
    //         localData.notifyBoardUpdated();
    //     });
    // };

    // let onClickKill = () => {
    //     localData.setInteraction(card => {
    //         localData.board.moveCardToZone(card, localData.board.players[card.ownerId].zones.graveyard);
    //         localData.notifyBoardUpdated();
    //     });
    // };

    let onClickInspect = () => {
        localData.setInteraction(card => {
            localData.setInspectedZone(card.getZone());
        });
    };

    let onClickStopInspect = () => {
        localData.setInspectedZone(null);
    };

    let onClickShuffle = () => {
        localData.setInteraction(card => {
            localData.board.shuffleZone(card.getZone());
            localData.notifyBoardUpdated();
        });
    };

    let onClickFlipZone = () => {
        localData.setInteraction(targetCard => {
            for (let card of targetCard.getZone()?.cards ?? []) {
                localData.board.flipCard(card);

                for (let attachedCard of card.attachedCards) {
                    localData.board.flipCard(attachedCard);
                }
            }

            localData.notifyBoardUpdated();
        });
    };

    let onClickToggleGlobalVision = () => {
        localData.globalVision = !localData.globalVision;
        localData.forceRefresh();
    };

    let onClickResetBoard = () => {
        let confirmed = confirm('Reset board to its original state?');

        if (!confirmed) {
            return;
        }

        let deckIds = localData.board.deckIds!;

        localData.board = new Board(localData.project);
        localData.board.initDecks(localData.project, deckIds);
        localData.notifyBoardUpdated();
    };

    let onClickReduce = () => {
        App.instance.setState({
            playPanelOpen: false,
        });
    };

    let onClickClose = () => {
        App.instance.setState({
            initBoardCallback: null,
            playPanelOpen: false,
        });
    };

    return (
        <Row style={style}>
            <Row justifyContent='start' gap={5} grow>
                <Button
                    style={buttonStyle}
                    label={`Toggle global vision (${localData.globalVision ? 'on' : 'off'})`}
                    onClick={onClickToggleGlobalVision}
                    // shortcut='KeyT'
                    // tooltip={`Toggle global vision (currently ${localData.globalVision ? 'on' : 'off'})`}
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Reset board'
                    onClick={onClickResetBoard}
                    // shortcut='KeyT'
                    // tooltip={`Toggle global vision (currently ${localData.globalVision ? 'on' : 'off'})`}
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Inspect (I)'
                    onClick={onClickInspect}
                    shortcut='KeyI'
                    // tooltip='Inspect zone (I)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Stop inspect zone'
                    onClick={onClickStopInspect}
                    shortcut='Escape'
                    disabled={hasOngoingAction}
                    hidden
                />
                <Button
                    style={buttonStyle}
                    label='Shuffle (S)'
                    onClick={onClickShuffle}
                    shortcut='KeyS'
                    // tooltip='Shuffle zone (S)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Flip all (s-F)'
                    onClick={onClickFlipZone}
                    shortcut='Shift_KeyF'
                    // tooltip='Flip all cards in zone (Shift F)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Flip (F)'
                    onClick={onClickFlip}
                    shortcut='KeyF'
                    // tooltip='Flip card (F)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Tap (T)'
                    onClick={onClickRotate}
                    shortcut='KeyT'
                    // tooltip='Exhaust card (E)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Reveal (R)'
                    onClick={onClickReveal}
                    shortcut='KeyR'
                    // tooltip='Reveal card (R)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Mark (M)'
                    onClick={onClickMark}
                    shortcut={['KeyM', 'Semicolon']}
                    // tooltip='Mark card (M)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Clear marks (C)'
                    onClick={onClickClear}
                    shortcut='KeyC'
                    // tooltip='Clear marks (C)'
                    disabled={hasOngoingAction}
                />
                {/* <Button
                    style={buttonStyle}
                    label='Draw'
                    onClick={onClickDraw}
                    shortcut='KeyD'
                    tooltip='Move card to hand (D)'
                    disabled={hasOngoingAction}
                />
                <Button
                    style={buttonStyle}
                    label='Kill'
                    onClick={onClickKill}
                    shortcut='KeyK'
                    tooltip='Move card to graveyard (K)'
                    disabled={hasOngoingAction}
                /> */}
            </Row>
            <Row justifyContent='end' gap={5} grow>
                <BoardLinkComponent
                    isGuest={isGuest}
                    localData={localData}
                />
                <Button
                    label='Reduce'
                    onClick={onClickReduce}
                    disabled={hasOngoingAction}
                />
                <Button
                    label='End game'
                    onClick={onClickClose}
                    disabled={hasOngoingAction}
                />
            </Row>
        </Row>
    );
};